import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Box, Typography, Button, Grid, Checkbox, Divider, IconButton, TextField, CircularProgress } from "@mui/material";
import { Info } from "@mui/icons-material";

import ScheduleIcon from '@mui/icons-material/Schedule';
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { DataLayout, DrawerControl, FieldControl, Log, MetaDrivenTable, StatusChip, DialogBox } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";
import { useGetStakeholders } from "../../../shared/hooks";
import { Reminder } from "./Reminder";
import { useFetchJeopardyDetails } from "../hooks/useFetchJeopardyDetails";
import { usePostComments } from "../../../mutations";

import { useJeopardy } from "../hooks/useJeopardy";
import { useNotify } from "../hooks/useNotify";
import { useNavigation } from "../../../hooks/useNavigation";
import { useMarkAsRead } from "../hooks/useMarkAsRead";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useSearchProjects } from "../../projects/mutations";

export const JeopardyDetail = () => {

    const { jeopardyId } = useParams();
    const { data: jeopardyData, isLoading, refetch } = useFetchJeopardyDetails(jeopardyId);

    const searchProjects = useSearchProjects();

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                await searchProjects.mutateAsync({
                    params:
                        jeopardyData.portfolio_id !== null ? { portfolio_id: jeopardyData.portfolio_id } :
                            jeopardyData.programme_id !== null ? { programme_id: jeopardyData.programme_id } :
                                { programme_id: jeopardyData.programme_id }
                });
            } catch (error) {
                console.error("Error fetching project options:", error);
            }
        };
        if (jeopardyData.project_id === null) {
            fetchOptions();
        }
    }, [isLoading]);

    const { goToProjectDetails } = useNavigation();

    const { updateAccountablePersons, updateJeopardyTask } = useJeopardy();

    const [assignedPersons, setAssignedPersons] = useState(false);
    const [isUpdatingPerson, setIsUpdatingPerson] = useState(false);

    const [reminderOpen, setReminderOpen] = useState(false);

    const payload = () => {
        if (jeopardyData.project_id) {
            return { project_id: jeopardyData.project_id };
        } else if (jeopardyData.portfolio_id) {
            return { portfolio_id: jeopardyData.portfolio_id };
        } else if (jeopardyData.programme_id) {
            return { programme_id: jeopardyData.programme_id };
        }
    };

    const { data: userOptions, isLoading: isLoadingUsers } = useGetStakeholders({ params: payload() });

    const headers = [
        { alias_name: "name", label: "Name", display_order: 8, is_show: true },
        { alias_name: "phone", label: "Phone", display_order: 8, is_show: true },
        { alias_name: "email", label: "Email", display_order: 8, is_show: true },
    ];

    // Handlers
    const handleOpenAssignDialog = () => setAssignedPersons(true);
    const handleCloseAssignDialog = () => setAssignedPersons(false);

    const [taskNote, setTaskNote] = useState({
        note: "",
        role: "",
    });

    const [taskError, setTaskError] = useState(false);
    const [isUpdatingTask, setIsUpdatingTask] = useState(false);
    const [openUpdateDialog, setOpenUpdateDialog] = useState<{ status: boolean; task: { task_text: string, value: boolean, task_id: string } | null; }>({ status: false, task: null });

    const handleOpenUpdateDialog = (task: { task_text: string, value: boolean, task_id: string }) => setOpenUpdateDialog({ status: true, task });
    const handleCloseUpdateDialog = () => setOpenUpdateDialog({ status: false, task: null });

    const handleSubmitTask = async () => {
        if (taskNote.note.trim() === "") {
            setTaskError(true);
            return;
        }
        setTaskError(false);
        if (!jeopardyId) return;

        setIsUpdatingTask(true);
        try {
            await updateJeopardyTask({
                jeopardyId: jeopardyId,
                payload: {
                    notes: taskNote.note,
                    jeopardy_tasks: [
                        {
                            task_id: openUpdateDialog.task?.task_id || "",
                            value: !openUpdateDialog.task?.value,
                        }
                    ]
                }
            });
            handleCloseUpdateDialog();
            refetch();
        } finally {
            setIsUpdatingTask(false);
        }
    };

    const handleUpdatePerson = async () => {
        setIsUpdatingPerson(true);
        try {
            await updateAccountablePersons({
                jeopardyId: jeopardyId,
                payload: userOptions?.data.filter((role: any) =>
                    assignUser.role === role.role_id
                ).flatMap((role: any) =>
                    role.users.filter((user: any) =>
                        assignUser.user === user.user_id
                    )).map((user: any) => ({
                        role: userOptions?.data?.flatMap((role: any) =>
                            role.role_id === assignUser.role
                                ? role.role_name
                                : ""
                        )[0],
                        user_id: assignUser.user,
                        email: user.email,
                    }
                    ))
            });
            handleCloseAssignDialog();
            refetch();
        } finally {
            setIsUpdatingPerson(false);
        }
    };

    const handleOpenReminder = () => setReminderOpen(true);
    const handleCloseReminder = () => setReminderOpen(false);

    const { getFilterOptions } = useDisplayField([]);

    const [assignUser, setAssignUser] = useState<{
        role: string | null;
        user: string | null;
    }>({
        role: null,
        user: null
    });

    const [actionNote, setActionNote] = useState("");
    const [isUpdatingAction, setIsUpdatingAction] = useState(false);

    const postComments = usePostComments();

    const handleUpdateAction = async () => {
        if (!jeopardyId) return;
        setIsUpdatingAction(true);
        try {
            const payload = {
                "comment_topic": "JEOPARDY_COMMENT",
                "object_id": jeopardyId,
                "object_type": "JEOPARDY",
                "comment_text": actionNote,
            };
            await postComments.mutateAsync(payload);
            setActionNote("");
            refetch();
        } finally {
            setIsUpdatingAction(false);
        }
    };

    const { isNotify, notifyData, handleOpenNotify, handleCloseNotify, setNotifyData, handleSubmitNotify } = useNotify({ refetch });
    const { isOpen, handleOpen, handleClose, completeNotes, setCompleteNotes, error, handleSubmitMarkAsRead } = useMarkAsRead({ refetch });

    if (isLoading || !jeopardyData) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        );
    };

    return (
        <Box px={2} py={2} display="flex" flexDirection="column" height="100%" overflow="hidden">

            {/* Main Content */}
            <Grid container spacing={2} height="100%" overflow="hidden">
                <Grid size={{ xs: 12, md: 9 }} height="100%" sx={{ overflowY: "auto", pr: 1 }}>
                    <Grid container spacing={2} mb={2}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                                <Typography variant="subtitle1" color="text.primary">
                                    {jeopardyData.category}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {jeopardyData.sub_category}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {jeopardyData.project_description}
                                </Typography>
                                <Box display="flex" alignItems="center" justifyContent="flex-start" width={'100%'} gap={2}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="caption" color="text.secondary">
                                            Reported On :
                                        </Typography>
                                        <Typography variant="caption" color="text.primary">
                                            {formatDate(jeopardyData.reported_on)}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="caption" color="text.secondary">
                                            Due Date :
                                        </Typography>
                                        <Typography variant="caption" color="text.primary">
                                            {formatDate(jeopardyData.due_date)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display={'flex'} alignItems="start" justifyContent="flex-end" mb={2}>
                                <Box display="flex" alignItems="center" justifyContent={"flex-end"} flexWrap={"wrap"} gap={1} mb={2}>
                                    <Button startIcon={<NotificationsActiveIcon />} variant="outlined" onClick={handleOpenNotify} size="small">Notify</Button>
                                    <Button startIcon={<ScheduleIcon />} variant="outlined" onClick={handleOpenReminder} size="small">Reminder</Button>
                                    {
                                        jeopardyData.actions.includes("mark_as_closed") && (
                                            <Button variant="contained" onClick={handleOpen} size="small">Mark as Closed</Button>
                                        )
                                    }
                                </Box>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                                <Typography variant="body2" color={jeopardyData.severity === "Average" ? "warning.main" : "error.main"} display="flex" alignItems="center">
                                    <Info sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                    {jeopardyData.severity}
                                </Typography>
                                {jeopardyData.overdue && <StatusChip label="Overdue" color="red" />}
                                <StatusChip label={jeopardyData.status} color={jeopardyData.status === "ASSIGNED" ? 'green' : 'red'} />
                            </Box>
                        </Grid>
                    </Grid>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <DataLayout
                            title="Accountable Person"
                            bgVariant="filled"
                            action={
                                <IconButton size="small" onClick={handleOpenAssignDialog}>
                                    <PersonAddAlt1Icon sx={{ fontSize: 16 }} />
                                </IconButton>
                            }
                        >
                            {
                                !jeopardyData.accountable_person ? (
                                    <Typography variant="body2" color="text.primary" textAlign="center" px={2} my={2}>
                                        No accountable person assigned.
                                    </Typography>
                                ) :
                                    <MetaDrivenTable
                                        headers={headers}
                                        data={[
                                            {
                                                name: jeopardyData.accountable_person,
                                                phone: jeopardyData.accountable_person_phone || "N/A",
                                                email: jeopardyData.accountable_person_email,
                                            }
                                        ]}
                                        dataMetrics={{}}
                                        sortDisabled
                                        small
                                    />
                            }
                        </DataLayout>
                        <DataLayout title="What needs to be done" bgVariant="filled">
                            <Box py={1} px={1}>
                                {jeopardyData?.jeopardy_task?.length ? (
                                    jeopardyData?.jeopardy_task?.map(
                                        (a: { task_text: string, value: boolean, task_id: string }, i: number) => (
                                            <Box key={i} display="flex" alignItems="center">
                                                <Checkbox checked={a.value} disabled={a.value} size="small" onClick={() => handleOpenUpdateDialog(a)} />
                                                <Typography variant="caption">{a.task_text}</Typography>
                                            </Box>
                                        )
                                    )
                                ) : (
                                    <Box display="flex" justifyContent="center">
                                        <Typography variant="body2" color="text.primary" textAlign="center" px={2}>No tasks found.</Typography>
                                    </Box>
                                )}
                            </Box>
                        </DataLayout>
                        <DataLayout title="Key Personnel">
                            <MetaDrivenTable
                                headers={[
                                    { alias_name: "role", label: "Role", display_order: 1, is_show: true },
                                    { alias_name: "name", label: "Name", display_order: 2, is_show: true },
                                    { alias_name: "phone", label: "Phone", display_order: 3, is_show: true },
                                    { alias_name: "email", label: "Email", display_order: 4, is_show: true },
                                ]}
                                data={
                                    userOptions?.data?.flatMap((role: any) =>
                                        role?.users?.map((user: any) => ({
                                            role: role.role_name,
                                            name: user.user_name,
                                            phone: user.phone || "N/A",
                                            email: user.email,
                                        }))
                                    ) || []
                                }
                                dataMetrics={{}}
                                sortDisabled
                                small
                            />
                        </DataLayout>
                        {
                            jeopardyData.project_id && (
                                <DataLayout title="Project Overview" bgVariant="filled">
                                    <MetaDrivenTable
                                        headers={[
                                            { alias_name: "project_title", label: "Project Name", display_order: 1, is_show: true },
                                            { alias_name: "project_description", label: "Project Description", display_order: 2, is_show: true },
                                        ]}
                                        data={[
                                            {
                                                project_title: jeopardyData.project_title,
                                                project_description: jeopardyData.project_description || "N/A",
                                            }
                                        ]}
                                        dataMetrics={
                                            {
                                                project_title: {
                                                    type: 'component',
                                                    element: (() => (
                                                        <Typography variant="inherit" color="primary.main" sx={{ cursor: 'pointer' }}
                                                            onClick={() => goToProjectDetails(jeopardyData.project_id!, jeopardyData.project_title)}
                                                        >
                                                            {jeopardyData.project_title}
                                                        </Typography>
                                                    ))
                                                }
                                            }
                                        }
                                        sortDisabled
                                        small
                                    />
                                </DataLayout>
                            )
                        }
                        {
                            !jeopardyData.project_id && (
                                <DataLayout title="Related Projects" bgVariant="filled">
                                    <MetaDrivenTable
                                        headers={[
                                            { alias_name: "title", label: "Project Name", display_order: 1, is_show: true },
                                            { alias_name: "description", label: "Project Description", display_order: 2, is_show: true },
                                        ]}
                                        data={searchProjects?.data?.data || []}
                                        dataMetrics={
                                            {
                                                title: {
                                                    type: 'component',
                                                    element: ((data: { title: string }) => (
                                                        <Typography variant="inherit" color="primary.main" sx={{ cursor: 'pointer' }} onClick={() =>
                                                            goToProjectDetails(jeopardyData.id!, data.title)
                                                        }>
                                                            {data.title}
                                                        </Typography>
                                                    ))
                                                }
                                            }
                                        }
                                        sortDisabled
                                        small
                                    />
                                </DataLayout>
                            )
                        }
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }} height={'100%'}>
                    <DataLayout title="Action history" bgVariant="filled" sx={{ pb: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Box pl={2} py={2} flex={1} sx={{ overflow: "auto" }} display={'flex'} flexDirection={'column'}>
                            <Log topic={['JEOPARDY', 'JEOPARDY_COMMENT']} sub_event={['']} object_id={jeopardyId} parent_id={undefined} variant="activity" />
                        </Box>
                        <Box px={2} flexShrink={0}>
                            <DataLayout title="Add Update" bgVariant="filled">
                                <Box p={2}>
                                    <TextField
                                        fullWidth
                                        label="Update"
                                        type="note"
                                        size="small"
                                        placeholder="Enter your notes"
                                        required
                                        value={actionNote}
                                        onChange={(e) => setActionNote(e.target.value)}
                                    />
                                    <Divider sx={{ my: 2 }} />
                                    <Box display="flex" justifyContent="flex-end" gap={2}>
                                        <Button
                                            variant="text"
                                            sx={{ color: "text.primary" }}
                                            onClick={() => { setActionNote("") }}
                                            disabled={isUpdatingAction}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="text"
                                            sx={{ color: "primary.main" }}
                                            startIcon={isUpdatingAction ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
                                            onClick={handleUpdateAction}
                                            disabled={isUpdatingAction || !actionNote.trim()}
                                        >
                                            {isUpdatingAction ? "Adding..." : "Add Update"}
                                        </Button>
                                    </Box>
                                </Box>
                            </DataLayout>
                        </Box>
                    </DataLayout>
                </Grid>
            </Grid>

            {/* Assign Accountable Person Dialog */}
            <DialogBox
                showModal={assignedPersons}
                title="Assign Accountable Person"
                subTitle=""
                onModalClose={handleCloseAssignDialog}
                actions={[
                    {
                        label: "Cancel",
                        icon: "reset" as const,
                        variant: "text" as const,
                        handleClick: handleCloseAssignDialog,
                    },
                    {
                        label: "Complete",
                        icon: "success" as const,
                        variant: "contained" as const,
                        handleClick: handleUpdatePerson,
                        disabled: isUpdatingPerson,
                        loading: isUpdatingPerson
                    },
                ]}
                children={
                    <Box display={'flex'} flexDirection={'column'} gap={2} width={450}>
                        <FieldControl
                            keyName="role"
                            label="Select Role"
                            variant="select"
                            options={
                                userOptions?.data?.flatMap((role: any) => ({
                                    value: role.role_id,
                                    label: role.role_name,
                                })) || []
                            }
                            loading={isLoadingUsers}
                            value={assignUser.role}
                            onChange={(_key: string, value: any) => {
                                setAssignUser((prev) => ({
                                    ...prev,
                                    role: value
                                }))
                            }}
                        />
                        <FieldControl
                            keyName="assigned_to"
                            label="Assigned to"
                            variant="select"
                            options={
                                userOptions?.data?.filter((role: any) =>
                                    assignUser.role
                                        ? role.role_id === assignUser.role
                                        : true
                                ).flatMap((role: any) =>
                                    role?.users?.map((user: any) => ({
                                        value: user.user_id,
                                        label: user.user_name,
                                    }))
                                ) || []
                            }
                            loading={isLoadingUsers}
                            value={assignUser.user}
                            onChange={(_key: string, value: any) => {
                                setAssignUser((prev) => ({
                                    ...prev,
                                    user: value
                                }))
                            }}
                        />
                    </Box>
                }
            />

            {/* Update Details Dialog */}
            <DialogBox
                showModal={openUpdateDialog.status}
                title="Update Details"
                subTitle=""
                onModalClose={handleCloseUpdateDialog}
                actions={[
                    {
                        label: "Cancel",
                        icon: "reset" as const,
                        variant: "text" as const,
                        handleClick: handleCloseUpdateDialog
                    },
                    {
                        label: "Complete",
                        icon: "success" as const,
                        variant: "contained" as const,
                        handleClick: handleSubmitTask,
                        disabled: isUpdatingTask,
                        loading: isUpdatingTask
                    },
                ]}
                children={
                    <Box width={450} display="flex" flexDirection="column" gap={2}>
                        <FieldControl
                            keyName="note"
                            label="Update Details"
                            variant="input"
                            value={taskNote.note}
                            onChange={(_key: string, value: string) => setTaskNote({ ...taskNote, note: value })}
                            error={taskError}
                            helperText={taskError ? "Update details cannot be empty." : ""}
                        />
                    </Box>
                }
            />

            {/* Reminder Drawer */}
            <DrawerControl
                isOpen={reminderOpen}
                handleClose={handleCloseReminder}
                title="Reminders"
                subTitle="Set a reminder for this risk"
                children={<Reminder jeopardyId={jeopardyId} />}
            />

            {/* Notify dialog */}
            <DialogBox
                showModal={isNotify}
                title="Notify Accountable Person"
                subTitle=""
                onModalClose={handleCloseNotify}
                fullWidth
                actions={[
                    {
                        label: "Cancel",
                        icon: "reset" as const,
                        variant: "text" as const,
                        handleClick: handleCloseNotify
                    },
                    {
                        label: "Complete",
                        icon: "success" as const,
                        variant: "contained" as const,
                        handleClick: () => handleSubmitNotify({ jeopardy_ref: jeopardyData.jeopardy_ref }),
                        disabled: isUpdatingTask,
                        loading: isUpdatingTask
                    },
                ]}
                children={
                    <Box display="flex" flexDirection="column" m={3}>
                        <FieldControl
                            keyName={"notrifyVia"}
                            label={"Select key personal"}
                            variant={"select"}
                            options={
                                userOptions?.data?.flatMap((role: any) =>
                                    role?.users?.map((user: any) => ({
                                        value: user.user_id,
                                        label: user.user_name,
                                    }))
                                ) || []
                            }
                            loading={isLoadingUsers}
                            onChange={(_key: string, value: any) => {
                                setNotifyData((prev) => ({
                                    ...prev,
                                    keyPerson: value
                                }))
                            }}
                            value={notifyData.keyPerson}
                            multiSelect
                        />
                        <Typography variant="subtitle2" mt={2}>
                            Notify via:
                        </Typography>
                        <FieldControl
                            keyName={"notrifyVia"}
                            label={"Notify Via"}
                            variant={"checkbox"}
                            onChange={(_key: string, value: any) => {
                                setNotifyData((prev) => ({
                                    ...prev,
                                    notifyVia: value
                                }))
                            }}
                            options={[
                                {
                                    value: 'SMS',
                                    label: 'SMS'
                                },
                                {
                                    value: 'EMAIL',
                                    label: 'Email'
                                }
                            ]}
                            value={notifyData.notifyVia}
                        />
                    </Box>
                }
            />

            {/* Mark as closed */}
            <DialogBox
                showModal={isOpen}
                title="Update Details"
                subTitle=""
                onModalClose={handleClose}
                actions={[
                    {
                        label: "Cancel",
                        icon: "reset" as const,
                        variant: "text" as const,
                        handleClick: handleClose
                    },
                    {
                        label: "Complete",
                        icon: "success" as const,
                        variant: "contained" as const,
                        handleClick: () => handleSubmitMarkAsRead(jeopardyId!),
                        disabled: isUpdatingTask,
                        loading: isUpdatingTask
                    },
                ]}
                children={
                    <Box width={450} display="flex" flexDirection="column" gap={2}>
                        <FieldControl
                            keyName="note"
                            label="Update Notes"
                            variant="select"
                            value={completeNotes}
                            error={error ? true : false}
                            helperText={error ? "Update details cannot be empty." : ""}
                            options={
                                getFilterOptions("JEOPARDY_CLOSURE_REASON")?.map((option) => ({
                                    value: option.key,
                                    label: option.display_text
                                }))
                            }
                            onChange={(_key: string, value: string) => setCompleteNotes(value)}
                        />
                    </Box>
                }
            />

        </Box>
    );
}